<template>
  <component
    :is="tag"
    v-bind="linkAttrs"
    :class="classes"
    :disabled="isNative ? (disabled || loading) : undefined"
    :aria-disabled="disabled || loading ? 'true' : undefined"
    @click="onClick"
  >
    <span v-if="loading" class="bj-btn__spinner" aria-hidden="true"></span>
    <slot />
  </component>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  variant: { type: String, default: 'burgundy' }, // burgundy | green | tan
  to: { type: [String, Object], default: null },
  href: { type: String, default: null },
  type: { type: String, default: 'button' },
  disabled: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  small: { type: Boolean, default: false }
})

const emit = defineEmits(['click'])

const inert = computed(() => props.disabled || props.loading)

const tag = computed(() => {
  if (props.to && !inert.value) return 'router-link'
  if (props.href && !inert.value) return 'a'
  return 'button'
})

const isNative = computed(() => tag.value === 'button')

const linkAttrs = computed(() => {
  if (tag.value === 'router-link') return { to: props.to }
  if (tag.value === 'a') {
    const external = /^https?:/i.test(props.href || '')
    return {
      href: props.href,
      ...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})
    }
  }
  return { type: props.type }
})

const classes = computed(() => [
  'bj-btn',
  `bj-btn--${props.variant}`,
  { 'bj-btn--sm': props.small, 'bj-btn--disabled': inert.value }
])

function onClick (event) {
  if (inert.value) {
    event.preventDefault()
    return
  }
  emit('click', event)
}
</script>

<style scoped>
.bj-btn__spinner {
  width: 14px;
  height: 14px;
  margin-right: 10px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: bj-spin 0.7s linear infinite;
}
@keyframes bj-spin { to { transform: rotate(360deg); } }
</style>
